import _ from 'lodash'
import { useCallback, useEffect, useState, useRef } from 'react'
import { Lock } from 'react-feather'
import { connect } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import { withRouter } from 'react-router'
import Sticky from 'react-sticky-el'
import { toast } from 'react-toastify'
import { Button, Col, Spinner } from 'reactstrap'
import { compose } from 'redux'
import { clearCart, dispatchSweetAlert, sendCartToGreenwing, fetchDiscounts } from '../../actions'
import { cartItemsRequireCheckForApproval, returnCartItemsRequireApprovalError } from '../Cart/cartFunctions'
import ClearCartButton from '../Cart/ClearCartButton'
import {
  setSubtotal,
  calculateCartSubtotal,
  setCheckoutLoading,
  calculateCartSetupCharge,
  setSetupCharge,
  setErrors,
  setCustomCheckoutFee,
  validateCheckoutForm,
  fetchGrandTotal,
  resolveUnitPrice
} from '../Checkout/checkoutFunctions'
import dollar_price from '../general/DollarPrice'
import CheckoutConfirm from './CheckoutConfirm'
import useRecalculateGrandTotal from './useRecalculateGrandTotal'

const OrderSummary = props => {
  const [canProceed, setCanProceed] = useState(false)
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [checkoutErrorMessage, setCheckoutErrorMessage] = useState('')
  const [isZoomedIn, setIsZoomedIn] = useState(false)
  const [checkoutFormIsValid, setCheckoutFormIsValid] = useState(false)
  const [checkoutHasNoErrors, setCheckoutHasNoErrors] = useState(true)

  const punchoutFormRef = useRef(null)

  useRecalculateGrandTotal(props)

  const handlePunchout = useCallback(async () => {
    if (props.cartItems.length > 0) {
      const numberOfDiscountsApplied = _.filter(props.appliedPromotions, promo => promo.type === 'discount').length

      // Apply promotions to cart items like in checkout flow
      const cartForForm = props.cartItems.map(cartItem => {
        // Find if this item has any discounts from promotions
        const appliedPromotion = props.appliedPromotions[0]
        let discountedPrice = resolveUnitPrice(cartItem.priceData)

        if (appliedPromotion && appliedPromotion.updated_cart) {
          const updatedCartItem = appliedPromotion.updated_cart.find(
            updatedItem => updatedItem.cart_item_id === cartItem.cart_item_id
          )
          if (updatedCartItem && updatedCartItem.promo_data) {
            discountedPrice = updatedCartItem.promo_data.discounted_price
          }
        }

        return {
          ...cartItem,
          promo_data: {
            item_price: resolveUnitPrice(cartItem.priceData, false), // Original price without discounts
            discounted_price: discountedPrice,
            quantity: cartItem.quantity
          }
        }
      })

      const formDataDiscount = new FormData()
      formDataDiscount.append('applied_count', numberOfDiscountsApplied)
      formDataDiscount.append('subtotal', props.subtotal)
      formDataDiscount.append('shipping_cost', props.shippingQuote)
      formDataDiscount.append('cart_items', JSON.stringify(cartForForm))

      // Add promotion data if any promotions are applied
      if (props.appliedPromotions.length > 0) {
        const promotion = props.appliedPromotions[0]
        formDataDiscount.append('promotion_id', promotion.promotion_id)
        formDataDiscount.append('subtotal_reduction', props.checkout.subtotalReduction)
        formDataDiscount.append('shipping_reduction', props.checkout.shippingReduction)
        formDataDiscount.append('shipping_discount_in_cents', props.checkout.shippingReductionEncrypted)
        formDataDiscount.append('product_reduction', props.checkout.productReduction)
        formDataDiscount.append('updated_cart', JSON.stringify(promotion.updated_cart))
      }

      const response = await props.sendCartToGreenwing(formDataDiscount, props.currentLocation)

      if (!response) return // Error already handled by sendCartToGreenwing

      if (response?.cartdata && punchoutFormRef.current) {
        const cartdataInput = punchoutFormRef.current.querySelector('input[name="cartdata"]')
        if (!cartdataInput) {
          console.error('Cart data input not found')
          return
        }
        cartdataInput.value = response.cartdata
        punchoutFormRef.current.submit()

        await props.clearCart(props.currentLocation)
        toast.success('Your purchase request has been submitted')
        props.history.push('/')
      }
    }
  }, [
    props.cartItems,
    props.appliedPromotions,
    props.subtotal,
    props.shippingQuote,
    props.sendCartToGreenwing,
    props.currentLocation,
    props.checkout.subtotalReduction,
    props.checkout.shippingReduction,
    props.checkout.shippingReductionEncrypted,
    props.checkout.productReduction,
    props.clearCart,
    props.history,
    punchoutFormRef
  ])
  const { checkout, customerGroup, cartItems } = props

  const { selectedCustomerAddress, checkoutLoading, taxLoading, shippingLoading, creditsUsed, youPay, errors } =
    checkout

  // watch checkoutErrors and set message appropriately
  useEffect(() => {
    const checkoutErrorsExist = Object.values(errors).some(v => v === true || (typeof v === 'object' && !_.isEmpty(v)))
    if (checkoutErrorsExist) {
      setCheckoutHasNoErrors(false)
      if (Object.keys(errors).some(key => key.includes('multiAddress') && errors[key] === true)) {
        setCheckoutErrorMessage('Please see multi-address selections.')
      } else if (Object.keys(errors).some(key => key.includes('requireApprovalRule') && errors[key] === true)) {
        setCheckoutErrorMessage('Cart cannot contains items that both require approval and do not require approval.')
      } else {
        setCheckoutErrorMessage('')
      }
    } else {
      setCheckoutErrorMessage('')
      setCheckoutHasNoErrors(true)
    }
  }, [errors])

  const paymentMethodValid = () => {
    let paymentMethodValid = false
    switch (checkout.paymentMethod) {
      case 'credit_card':
        paymentMethodValid = !(
          checkout.ccData.card_number === '' ||
          checkout.ccData.card_cvc === '' ||
          checkout.ccData.card_expiry === '' ||
          checkout.ccData.card_name === ''
        )
        break
      case 'net30':
        paymentMethodValid = checkout.net30Confirmed
        break
    }
    return youPay === 0 || paymentMethodValid // payment is always valid for free orders
  }

  // check if user can proceed with checkout
  useEffect(() => {
    // each step may have to check if payment method is net-30
    // if payment is required and no billing address
    const billingAddressValid =
      !(youPay > 0 && _.isEmpty(checkout.selectedBillingAddress)) ||
      (checkout.paymentMethod === 'net30' && checkout.net30Confirmed)

    // if customer address is empty - unless all cart items are digital
    const customerAddressValid =
      !_.isEmpty(selectedCustomerAddress) || cartItems.every(cartItem => cartItem.shippable_type === 'Digital Delivery')

    // if billing address is empty and there is a balance due and not net30

    const paymentValid = paymentMethodValid()

    // if there are any checkout errors (checkout.errors)
    const checkoutErrorsExist = Object.values(errors).some(v => v === true || (typeof v === 'object' && !_.isEmpty(v)))

    // if there are any legacy errors (only shipping)
    let legacyErrorsExist = false
    if (props.reduxErrors['shipping_errors']) {
      legacyErrorsExist = true
    }

    // Determine if checkout is allowed based on basic validation checks
    let proceedToCheckoutAllowed =
      customerAddressValid &&
      billingAddressValid &&
      paymentValid &&
      !checkoutErrorsExist &&
      !legacyErrorsExist &&
      checkoutFormIsValid &&
      checkoutHasNoErrors

    // Restrict checkout if credit card usage is hidden, Net 30 is not allowed,
    // and the user has a balance due without alternative credits/payment
    if (
      customerGroup.hide_credit_card &&
      !customerGroup.net_30_payments &&
      youPay > 0 &&
      !checkout.creditsUsed.lmsPoints &&
      !checkout.creditsUsed.accountCredits &&
      !checkout.creditsUsed.giftCard &&
      !checkout.creditsUsed.coopCredits &&
      !checkout.net30Confirmed
    ) {
      proceedToCheckoutAllowed = false
    }

    // Restrict checkout if credit card usage is hidden but Net 30 payments are allowed,
    // but net30 is not confirmed
    if (customerGroup.hide_credit_card && customerGroup.net_30_payments && youPay > 0 && !checkout.net30Confirmed) {
      proceedToCheckoutAllowed = false
    }
    setCanProceed(proceedToCheckoutAllowed)
  }, [
    checkout.ccData,
    selectedCustomerAddress,
    props.selectedBillingAddress,
    checkout.creditsUsed,
    youPay,
    errors,
    props.reduxErrors,
    checkout.net30Confirmed,
    checkoutFormIsValid,
    checkoutHasNoErrors,
    checkout.contactEmail
  ])

  // recalculate subtotal/setup charge if cartItems changes
  useEffect(() => {
    props.setSubtotal(calculateCartSubtotal(cartItems))
    props.setSetupCharge(calculateCartSetupCharge(cartItems))

    // if any cart items have max quantity while user is a guest, prevent checkout
    if (!props.currentUser.id && cartItems.some(cartItem => cartItem.max_quantity)) {
      props.setErrors({ maxQuantity: true })
    } else if (props.currentUser.id && cartItems.some(cartItem => cartItem.max_quantity)) {
      props.setErrors({ maxQuantity: false })
    }
  }, [cartItems])

  // Set custom checkout fee from location
  useEffect(() => {
    if (props.currentLocation) {
      props.setCustomCheckoutFee(props.currentLocation.custom_checkout_fee)
    }
  }, [props.currentLocation])

  // Check if cart items contain both requires approval and does not require approval
  useEffect(() => {
    if (cartItems && cartItems.length > 0 && cartItemsRequireCheckForApproval(null, cartItems, props.currentLocation)) {
      props.returnCartItemsRequireApprovalError(cartItems, null)
      props.setErrors({ requireApprovalRule: true })
    } else {
      props.setErrors({ requireApprovalRule: false })
    }
  }, [cartItems])

  const detectZoom = () => {
    const zoomLevel = Math.round(window.devicePixelRatio * 100)
    if (zoomLevel > 100) {
      setIsZoomedIn(true)
    } else {
      setIsZoomedIn(false)
    }
  }

  useEffect(() => {
    window.addEventListener('resize', detectZoom)
    return () => window.removeEventListener('resize', detectZoom)
  }, [detectZoom])

  // validate checkout form
  useEffect(() => {
    const checkoutFormValid = validateCheckoutForm(props.checkoutFormData)
    setCheckoutFormIsValid(checkoutFormValid)
  }, [props.checkoutFormData])

  // --- Punchout Discount Fetch Logic ---
  useEffect(() => {
    if (
      props.session?.punchout_buyer &&
      props.cartItems.length > 0 &&
      _.filter(props.appliedPromotions, promo => promo.type === 'discount').length === 0
    ) {
      // Build FormData for discounts
      const cartForForm = props.cartItems.map(cartItem => {
        const itemPrice = resolveUnitPrice(cartItem.priceData, false)
        return {
          ...cartItem,
          promo_data: {
            item_price: itemPrice,
            discounted_price: itemPrice,
            quantity: cartItem.quantity
          }
        }
      })
      const formDataDiscount = new FormData()
      formDataDiscount.append('applied_count', 0)
      formDataDiscount.append('subtotal', props.subtotal)
      formDataDiscount.append('shipping_cost', props.shippingQuote)
      formDataDiscount.append('cart_items', JSON.stringify(cartForForm))
      props.fetchDiscounts(formDataDiscount)
    }
  }, [props.session?.punchout_buyer, props.cartItems, props.subtotal, props.shippingQuote])

  const proceedToCheckout = () => {
    const user_id = props.currentUser.id

    // if user is not logged in, show Toast error & redirect
    // only if guest checkout is not enabled for the portal
    if (!user_id && props.portal.checkout_mode === 'registered_only') {
      toast.info('You need to Sign In to proceed to checkout!', {
        position: toast.POSITION.TOP_CENTER,
        type: toast.TYPE.SUCCESS,
        className: 'address-toast',
        autoClose: 3000
      })
      props.history.push(`/${props.portal.custom_login_url}`)
    } else if (errors.stock) {
      props.dispatchSweetAlert({
        type: 'error',
        alertMessage: 'Some items are out of stock! Please remove the highlighted items from your basket to continue.',
        showCancel: false,
        confirmBtnCssClass: 'mf-primary-btn alert-width-btn'
      })
    } else {
      // check stock. if stock check is ok, proceed to checkout, else set state errors
      props.history.push('/check-out')
    }
  }

  // check for errors and show order confirmation modal
  const handlePlaceOrder = () => {
    if (youPay > 0 && checkout.paymentMethod === 'credit_card') {
      // check CC details here
      const ccData = checkout.ccData
      const mm = ccData.card_expiry.substring(0, 2)
      const yy = ccData.card_expiry.substring(2)
      const now = new Date()
      const thisCentury = `${now.getFullYear()}`.substring(0, 2)
      const monthIndex = parseInt(mm) - 1
      const expiryDate = new Date(`${thisCentury}${yy}`, monthIndex)
      // remove spaces from card number
      ccData.card_number = ccData.card_number.replace(/\s/g, '')
      const ccError =
        expiryDate < now ||
        ccData.card_number.length < 12 ||
        ccData.card_number.length > 19 ||
        !ccData.card_name ||
        !ccData.card_cvc ||
        !ccData.card_expiry
      if (ccError) {
        props.dispatchSweetAlert({
          type: 'error',
          alertMessage: 'Please check your card details.',
          showCancel: false,
          confirmBtnCssClass: 'mf-primary-btn alert-width-btn mr-0'
        })
        return
      }
    }
    setShowConfirmationModal(true)
  }

  const displayCreditsUsed = (label, amount) => {
    return (
      <Col lg="12" className="flex-space-between m-0 green-text">
        <h6>{label}:</h6>
        <h6>
          {checkout.checkoutLoading ||
          (checkout.shippingLoading && !_.isEmpty(selectedCustomerAddress)) ||
          checkout.taxLoading ? (
            <Spinner />
          ) : (
            <>-{dollar_price(amount)}</>
          )}
        </h6>
      </Col>
    )
  }

  const displayPriceReduction = (discountType, amount, showHorizontalRule = false) => {
    return (
      <>
        <Col lg="12" className="flex-space-between m-0 green-text">
          <h6>{discountType} Discount:</h6>
          <h6>-{dollar_price(amount)}</h6>
        </Col>
        {showHorizontalRule ? <hr className="w-100" /> : null}
      </>
    )
  }

  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 992px)' })

  // CALCULATES HEIGHT OF SCROLL DOWN MENU AND ADDS MARGIN ON STICKY SIDEBAR
  const fixedHeaderElement = document.querySelector('.iron-fixed-header')
  const menuHeight = fixedHeaderElement !== null ? fixedHeaderElement.offsetHeight : '100px'
  const screenHeight = window.screen.availHeight
  const responseHeight = screenHeight !== null && screenHeight <= 770 ? true : false

  const allItemsDigitalDelivery = cartItems.every(cartItem => cartItem.shippable_type === 'Digital Delivery')
  const shippingAddressRequired = !allItemsDigitalDelivery

  const apisLoading =
    checkout.checkoutLoading || checkout.shippingLoading || checkout.taxLoading || checkout.cart_loading

  const errorsOnScreen = Object.values(props.errorsOnScreen).some(v => v !== null)

  const billingAddressNeeded =
    (checkout.paymentMethod === 'net30' && youPay > 0 && _.isEmpty(props.selectedBillingAddress)) ||
    (youPay === 0 && props.requireBillingAddressForFreeOrders && _.isEmpty(props.selectedBillingAddress))

  const placeYourOrderDisabled = errorsOnScreen || !canProceed || apisLoading || billingAddressNeeded
  const totalCostWithoutPaymentReduction = checkout.subtotal + checkout.taxQuote + checkout.shippingQuote

  return (
    <>
      <Sticky
        topOffset={0}
        stickyStyle={{
          top: menuHeight >= 90 ? '160px' : '100px',
          zIndex: '2'
        }}
        // onFixedToggle= {}
        disabled={isTabletOrMobile || responseHeight || isZoomedIn}
        className="m-auto order-summary-custom-css"
      >
        <div className="m-0 box-shadow white-background">
          <div className="ckout-header">
            <h5 className="m-0">Order Summary</h5>
          </div>
          {props.session?.punchout_buyer ? (
            <>
              <form ref={punchoutFormRef} method="POST" action={props.session?.return_url} style={{ display: 'none' }}>
                <input type="hidden" name="cartdata" />
              </form>
              {/* <Col lg="12" className="p-3">
                <label style={{ fontSize: '.875rem' }}>Enter Zip Code for Estimate (optional):</label>
                <div className="d-flex">
                  <Input
                    type="text"
                    placeholder="Zip Code"
                    style={{ flexBasis: '70%', borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                  />
                  <Button outline style={{ flexBasis: '30%', borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}>
                    Estimate
                  </Button>
                </div>
                <hr className={props.currentLocation.show_price ? 'w-100' : 'd-none'} />
              </Col> */}
              <Col lg="12" className={props.currentLocation.show_price ? 'flex-space-between m-0 p-3' : 'd-none'}>
                {props.isCheckout ? (
                  <>
                    <h6>Subtotal:</h6>
                    <h6>{dollar_price(props.subtotal)}</h6>
                  </>
                ) : (
                  <>
                    <h5>Subtotal:</h5>
                    <h5>{dollar_price(props.subtotal)}</h5>
                  </>
                )}
                <Button className="w-100 mf-secure-co-btn mt-2 mb-2 ckout-btn-custom-css" onClick={handlePunchout}>
                  <Lock />
                  &nbsp;&nbsp;Submit Purchase Request
                </Button>
                <br />
                <ClearCartButton />
              </Col>
            </>
          ) : (
            <Col lg="12" className="p-3">
              <hr className={props.currentLocation.show_price ? 'w-100' : 'd-none'} />

              {/* Set Up Charge */}
              <Col lg="12" className={props.currentLocation.show_price ? 'flex-space-between m-0 mb-1' : 'd-none'}>
                {props.setupCharge > 0 ? (
                  props.isCheckout ? (
                    <>
                      <h6>Setup Charge:</h6>
                      <h6>{dollar_price(props.setupCharge)}</h6>
                    </>
                  ) : (
                    <>
                      <h5>Setup Charge:</h5>
                      <h5>{dollar_price(props.setupCharge)}</h5>
                    </>
                  )
                ) : null}
              </Col>

              {/* Sub Total */}
              <Col lg="12" className={props.currentLocation.show_price ? 'flex-space-between m-0' : 'd-none'}>
                {props.isCheckout ? (
                  <>
                    <h6>Subtotal:</h6>
                    <h6>{dollar_price(props.subtotal)}</h6>
                  </>
                ) : (
                  <>
                    <h5>Subtotal:</h5>
                    <h5>{dollar_price(props.subtotal)}</h5>
                  </>
                )}
              </Col>

              {/* Checkout Form */}
              {!checkoutFormIsValid && props.isCheckout ? (
                <>
                  <hr className="w-100" />
                  <Col lg="12" className="flex-space-between m-0">
                    <h6 className="red-text">Please Complete All Required Fields In Checkout Information Form</h6>
                  </Col>
                </>
              ) : null}

              {/* Shipping */}
              {props.currentLocation.show_price && props.isCheckout ? (
                <>
                  <hr className="w-100" />
                  <Col lg="12" className="flex-space-between m-0">
                    {_.isEmpty(selectedCustomerAddress) && shippingAddressRequired ? (
                      <h6 className="red-text">
                        {props.currentUser.id
                          ? 'Please select a shipping address.'
                          : 'Please enter a shipping address.'}
                      </h6>
                    ) : (
                      <>
                        <h6>Shipping:</h6>
                        {props.shippingLoading === true ? <Spinner /> : <h6>{dollar_price(props.shippingQuote)}</h6>}
                      </>
                    )}
                  </Col>
                </>
              ) : null}

              {props.checkout.shippingReduction ||
              props.checkout.subtotalReduction ||
              props.checkout.productReduction ? (
                <hr />
              ) : null}

              {/* Shipping Reduction from promotion */}
              {props.checkout.shippingReduction
                ? displayPriceReduction('Shipping', props.checkout.shippingReduction)
                : null}

              {/* Subtotal Reduction from promotion */}
              {props.checkout.subtotalReduction
                ? displayPriceReduction('Subtotal', props.checkout.subtotalReduction)
                : null}

              {/* Product Reduction from promotion */}
              {props.checkout.productReduction
                ? displayPriceReduction('Product', props.checkout.productReduction, true)
                : null}

              {/* Tax */}
              {props.currentLocation.show_price && props.isCheckout && !_.isEmpty(selectedCustomerAddress) ? (
                <>
                  <Col lg="12" className="flex-space-between m-0">
                    <h6>Sales Tax:</h6>
                    {props.checkout.taxLoading || props.checkout.checkoutLoading || props.checkout.shippingLoading ? (
                      <Spinner />
                    ) : (
                      <h6>{dollar_price(props.taxQuote)}</h6>
                    )}
                  </Col>
                </>
              ) : null}

              {/* Extra Checkout Fee */}
              {props.currentLocation.show_price && props.isCheckout && props.customCheckoutFee > 0 ? (
                <>
                  <Col lg="12" className="flex-space-between m-0">
                    <h6>{props.currentLocation.custom_checkout_fee_alias}:</h6>
                    <h6>{dollar_price(props.customCheckoutFee)}</h6>
                  </Col>
                </>
              ) : null}

              {/* Error For Not Enough LMS Points */}
              {props.portal.use_lms_points &&
              totalCostWithoutPaymentReduction > props.userCredits.lms_points.remaining ? (
                <Col lg="12" className="flex-space-between m-0">
                  <hr className="w-100" />
                  <h6 className="red-text">Not Enough Points To Checkout</h6>
                </Col>
              ) : null}

              {/* For Checkout Only */}
              {/* Co-Op & You Pay */}
              {props.isCheckout ? (
                <>
                  {/* Credits Used */}
                  {creditsUsed.coopCredits || creditsUsed.accountCredits || creditsUsed.giftCard ? <hr /> : null}

                  {/* Coop Reduction */}
                  {creditsUsed.coopCredits ? (
                    <Col lg="12" className="flex-space-between m-0 green-text">
                      <h6>{localStorage.getItem('coop_def')}:</h6>
                      <h6>-{dollar_price(creditsUsed.coopCredits)}</h6>
                    </Col>
                  ) : null}

                  {props.checkout.creditsUsed.accountCredits
                    ? displayCreditsUsed(localStorage.getItem('account_credit_def'), creditsUsed.accountCredits)
                    : null}

                  {props.checkout.creditsUsed.giftCard ? displayCreditsUsed('Gift Card', creditsUsed.giftCard) : null}

                  {props.checkout.creditsUsed.lmsPoints ? displayCreditsUsed('Points', creditsUsed.lmsPoints) : null}

                  {props.currentLocation.show_price ? (
                    <Col lg="12" className="flex-space-between m-0 mt-3">
                      <h5>You Pay:</h5>
                      <h5>
                        {props.checkout.checkoutLoading ||
                        (props.checkout.shippingLoading && !_.isEmpty(props.checkout.selectedCustomerAddress)) ||
                        props.checkout.taxLoading ? (
                          <Spinner />
                        ) : (
                          <>{dollar_price(props.youPay)}</>
                        )}
                      </h5>
                    </Col>
                  ) : null}

                  {/* Place Order Button (opens confirmation alert box) */}
                  <Col sm="12">
                    <Button
                      className="w-100 mf-secure-co-btn mt-3 mb-2 ml-0 mr-0 ckout-btn-custom-css"
                      onClick={handlePlaceOrder}
                      disabled={placeYourOrderDisabled}
                    >
                      <Lock />
                      &nbsp;&nbsp;Place Your Order
                    </Button>
                    {checkoutErrorMessage ? <p style={{ color: 'red' }}>{checkoutErrorMessage}</p> : null}
                  </Col>
                </>
              ) : null}

              {!props.isCheckout && (
                <>
                  <Button
                    className="w-100 mf-secure-co-btn mt-2 mb-2 ckout-btn-custom-css"
                    onClick={proceedToCheckout}
                    disabled={
                      !!props.errorMessage ||
                      props.checkout.maxQuantityLoading ||
                      props.checkout.checkoutLoading ||
                      cartItemsRequireCheckForApproval(null, props.cartItems, props.currentLocation) ||
                      props.checkout.cart_loading
                    }
                  >
                    <Lock />
                    &nbsp;&nbsp;Proceed to Checkout
                  </Button>
                  <br />
                  <sub>{props.errorMessage}</sub>
                  <ClearCartButton />
                </>
              )}
            </Col>
          )}
        </div>
      </Sticky>

      {/* Order Confirmation Modal */}
      <CheckoutConfirm
        canProceed={canProceed}
        show={showConfirmationModal}
        hideModal={() => setShowConfirmationModal(false)}
      />
    </>
  )
}

const mapStateToProps = state => {
  return {
    cartItems: state.cartItems,
    appliedPromotions: state.checkout.appliedPromotions,
    grandTotal: state.checkout.grandTotal,
    subtotal: state.checkout.subtotal,
    portal: state.portal,
    errorMessage: state.checkout.errorMessage,
    taxQuote: state.checkout.taxQuote,
    shippingQuote: state.checkout.shippingQuote,
    errorsOnScreen: state.errors,
    checkout: state.checkout,
    selectedBillingAddress: state.checkout.selectedBillingAddress,
    reduxErrors: state.errors,
    shippingLoading: state.checkout.shippingLoading,
    currentLocation: state.currentLocation,
    userCredits: state.userCredits,
    setupCharge: state.checkout.setupCharge,
    currentUser: state.currentUser,
    customCheckoutFee: state.checkout.customCheckoutFee,
    checkoutForm: state.checkoutForm,
    checkoutFormData: state.checkout.checkoutFormData,
    customerGroup: state.customerGroup,
    requireBillingAddressForFreeOrders: state.currentLocation.require_billing_address_for_free_orders,
    session: state.session,
    youPay: state.checkout.youPay
  }
}

const mapDispatchToProps = {
  clearCart,
  dispatchSweetAlert,
  sendCartToGreenwing,
  fetchDiscounts,
  setSubtotal,
  setCheckoutLoading,
  setSetupCharge,
  returnCartItemsRequireApprovalError,
  setErrors,
  setCustomCheckoutFee,
  fetchGrandTotal
}

export default compose(withRouter, connect(mapStateToProps, mapDispatchToProps))(OrderSummary)
